import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Game } from "@/types";
import { RatingBadge } from "@/components/ui/RatingBadge";
import { GameImage } from "./GameImage";
import { MiniSparkline } from "./MiniSparkline";

interface GameCardProps {
  game: Game;
}

function formatPrice(n: number) {
  return `${n.toFixed(2)} €`;
}

type BadgeTone = "rose" | "emerald";

type BadgeKind = "all_time_low" | "lowest_90d" | "lowest_30d" | "drop_pct";

interface BadgeDescriptor {
  kind: BadgeKind;
  tone: BadgeTone;
  pct?: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function trackingAgeDays(trackingSince: string | null | undefined): number {
  if (!trackingSince) return 0;
  return Math.floor((Date.now() - new Date(trackingSince).getTime()) / DAY_MS);
}

// Badge precedence:
//   1. All-time low (rose) — requires ≥30 days of tracking history so the
//      claim is credible.
//   2. Server-computed dropPercentage (emerald) — matches the `biggest_drop`
//      sort exactly. Falls back to local sparkline math only when the server
//      didn't supply a value (older queries / shared types).
function buildBadge(game: Game): BadgeDescriptor | null {
  const s = game.priceHistorySummary;
  if (!s?.currentLow) {
    // Even without summary we may still have a server drop value.
    if (game.dropPercentage && game.dropPercentage >= 5) {
      return { kind: "drop_pct", tone: "emerald", pct: game.dropPercentage };
    }
    return null;
  }

  const ageDays = trackingAgeDays(s.trackingSince);

  if (
    s.allTimeLow != null &&
    Math.abs(s.currentLow - s.allTimeLow) < 0.01 &&
    ageDays >= 30
  ) {
    if (ageDays >= 90) return { kind: "all_time_low", tone: "rose" };
    if (ageDays >= 60) return { kind: "lowest_90d", tone: "rose" };
    return { kind: "lowest_30d", tone: "rose" };
  }

  if (game.dropPercentage && game.dropPercentage >= 5) {
    return { kind: "drop_pct", tone: "emerald", pct: game.dropPercentage };
  }

  if (game.priceSparkline && game.priceSparkline.length >= 2) {
    const first = game.priceSparkline[0];
    const last = game.priceSparkline[game.priceSparkline.length - 1];
    if (first > last && first > 0) {
      const pct = Math.round(((first - last) / first) * 100);
      if (pct >= 10) return { kind: "drop_pct", tone: "emerald", pct };
    }
  }

  return null;
}

const BADGE_CLASSES: Record<BadgeTone, string> = {
  rose: "bg-rose-600 text-white shadow-sm",
  emerald: "bg-emerald-600 text-white shadow-sm",
};

export function GameCard({ game }: GameCardProps) {
  const { t } = useTranslation();
  const imageUrl = game.imageUrl || game.thumbnailUrl;
  const summary = game.priceHistorySummary;
  const shopLow = summary?.currentLow ?? null;
  const listingLow = game.lowestPrice ?? null;
  // Prefer shop price as the primary headline; only fall back to a community
  // listing price when no shop is tracked.
  const currentLow = shopLow ?? listingLow;
  const shopName = summary?.currentLowShopName ?? null;
  const shopCount = game.trackedShopCount ?? 0;
  const badge = buildBadge(game);
  const listingsCount = game.activeListingsCount ?? 0;

  // Caption under the price: prefer the comparison framing ("Pigiausia per N
  // parduotuves") because it sells the price-tracker promise. Falls back to
  // the shop name when only 1 shop is tracked (the count adds no value there),
  // and to a community label when there's no shop tracking at all.
  let priceCaption: string;
  if (shopLow != null) {
    if (shopCount >= 2) {
      priceCaption = t("game_card.tracked_in_shops", { count: shopCount });
    } else if (shopName) {
      priceCaption = shopName;
    } else {
      priceCaption = t("game_card.lowest_now");
    }
  } else if (listingLow != null) {
    priceCaption = t("game_card.from_community");
  } else {
    priceCaption = t("game_card.lowest_now");
  }

  // When BOTH a shop and community listings exist, surface the community
  // "from" anchor — a one-glance comparison cue ("2 skelbimai nuo 5€") is far
  // more useful than a bare count.
  const showListingsAnchor =
    listingsCount > 0 && shopLow != null && listingLow != null;
  const listingsLine = showListingsAnchor
    ? t("game_card.used_listings_from", {
        count: listingsCount,
        price: (listingLow as number).toFixed(2),
      })
    : listingsCount > 0
      ? t("game_card.used_listings", { count: listingsCount })
      : null;

  const badgeLabel = badge
    ? badge.kind === "drop_pct"
      ? t("game_card.badge_drop_pct", { pct: badge.pct })
      : t(`game_card.badge_${badge.kind}`)
    : null;

  return (
    <Link
      to={`/games/${game.id}`}
      className="card group overflow-hidden transition-shadow hover:shadow-lg"
    >
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        <GameImage
          src={imageUrl}
          alt={game.title}
          className="h-full w-full object-contain transition-transform group-hover:scale-105"
        />
        {/* BGG rating chip: trust signal, corner placement, low visual weight */}
        {game.displayRating != null && (
          <span className="absolute right-2 top-2">
            <RatingBadge
              rating={game.displayRating}
              source={game.ratingSource ?? null}
            />
          </span>
        )}
        {/* Conditional commerce badge: only when meaningful */}
        {badge && badgeLabel && (
          <span
            className={`absolute left-2 top-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${BADGE_CLASSES[badge.tone]}`}
          >
            {badgeLabel}
          </span>
        )}
      </div>

      <div className="p-4">
        {/* Title — slightly larger so it doesn't fight with the price */}
        <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold leading-tight text-gray-900 group-hover:text-primary-800">
          {game.title}
        </h3>

        {/* Gameplay metadata — demoted to thin caption row */}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-gray-500">
          {game.playerRange && (
            <span>
              {t("game_card.players_short", { range: game.playerRange })}
            </span>
          )}
          {game.playerRange && game.playtimeRange && <span>·</span>}
          {game.playtimeRange && <span>{game.playtimeRange}</span>}
        </div>

        {/* Price — the loudest element on the card */}
        <div className="mt-3 flex items-end justify-between gap-2">
          <div className="min-w-0">
            {currentLow != null ? (
              <>
                <p className="text-2xl font-extrabold leading-none tracking-tight text-gray-900">
                  {formatPrice(currentLow)}
                </p>
                <p className="mt-1 truncate text-[11px] uppercase tracking-wider text-gray-500">
                  {priceCaption}
                </p>
              </>
            ) : (
              <p className="text-xs text-gray-400">
                {t("game_card.not_tracked")}
              </p>
            )}
          </div>
          {game.priceSparkline && game.priceSparkline.length >= 2 && (
            <MiniSparkline points={game.priceSparkline} className="shrink-0" />
          )}
        </div>

        {listingsLine && (
          <p className="mt-2 flex items-center gap-1 truncate border-t border-gray-100 pt-2 text-xs text-gray-500">
            <svg
              className="h-3 w-3 shrink-0 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {listingsLine}
          </p>
        )}
      </div>
    </Link>
  );
}
